// PRODUCTION REPORT — one page per production item: what went in, day by day.
//
// The owner's request (2026-10-16): a page per job "about the units put in the
// wall", always the same shape — the one-off report generated for The Reign.
// Five figures, one sentence, a bar per working day, then by month, by week and
// notes.
//
// THE INVARIANT: every figure, label and sentence on that page comes out of
// buildProductionReport below. The screen (js/admin/69-production-report.js)
// and the PDF it downloads are two renderers of the SAME object, so they can
// never print two different numbers for one job.
//
// Input is buildReportRows' output (production-audit.js) — the rows the
// Production tab itself lists, read from the same query — so this page counts
// exactly what that tab counts. Nothing here knows a trade: the item's name and
// unit come from the catalogue row on each line.
//
// Definitions (unit-tested in test/unit/production-report.test.mjs):
//  · working day   — a date with a report carrying the item (quantity > 0).
//                    Two crews on one date are one working day, summed.
//  · trailing avg  — units per working day over the 28 calendar days ending on
//                    that day; plotted only once a full 28-day window exists.
//  · last 4 weeks  — that same window ending on the last report date.
//  · reporting gap — a run of days with no report for the item, between two
//                    working days, holding 5 or more weekdays (Mon–Fri).
//  · weeks         — Monday to Sunday. Months — calendar months.
//
// Pure: no DOM, no network. Dates are 'YYYY-MM-DD' strings; arithmetic runs on
// UTC day numbers so no time zone or DST can move a day.

import { fmtNumber, fmtDate, fmtDateRange } from './format.js';
import { displayName, jobNumber } from './project-ref.js';
import { t } from './i18n.js';

const DAY_MS = 86400000;
const WINDOW = 28;          // the trailing average and "last 4 weeks", in calendar days
const GAP_WEEKDAYS = 5;     // a stretch with no report is a gap at 5 weekdays
const TOP_LABELS = 5;       // biggest days named on the chart…
const LABEL_SPACING = 4;    // …at least 4 days apart, so no two labels collide

const num = v => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const ISO = /^\d{4}-\d{2}-\d{2}/;
const dnOf = iso => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / DAY_MS;
const isoOf = dn => new Date(dn * DAY_MS).toISOString().slice(0, 10);
const dow = dn => (dn + 3) % 7;              // Mon 0 … Sun 6 (day 0 is Thu 1 Jan 1970)
const mondayOf = dn => dn - dow(dn);
const monthStartDn = dn => { const s = isoOf(dn); return dnOf(s.slice(0, 8) + '01'); };
const nextMonthDn = dn => {
  const s = isoOf(dn); const y = +s.slice(0, 4), m = +s.slice(5, 7);
  return Date.UTC(m === 12 ? y + 1 : y, m === 12 ? 0 : m, 1) / DAY_MS;
};

/** A count with no unit word prints as a bare number; anything else carries its unit. */
const plainUnit = unit => !unit || unit === 'count';

/** A quantity as the page prints it: whole at 10 and over, one decimal under — never "4.0". */
export function fmtQty(n) {
  const v = num(n);
  const dp = Math.abs(v) >= 10 || Number.isInteger(Math.round(v * 10) / 10) ? 0 : 1;
  return fmtNumber(v, dp);
}
const withUnit = (n, unit) => fmtQty(n) + (plainUnit(unit) ? '' : ' ' + unit);

/**
 * The items this job has production for, most working days first — the
 * chooser's order, and its default (the first one).
 */
export function reportItems(rows) {
  const by = new Map();
  for (const r of rows || []) {
    if (!ISO.test(r.date || '')) continue;
    for (const i of r.items || []) {
      if (!i.materialTypeId || !(num(i.quantity) > 0)) continue;
      let e = by.get(i.materialTypeId);
      if (!e) by.set(i.materialTypeId, e = { materialTypeId: i.materialTypeId, name: i.name, unit: i.unit,
        known: i.known !== false, dates: new Set(), quantity: 0 });
      e.dates.add(r.date.slice(0, 10));
      e.quantity += num(i.quantity);
    }
  }
  return [...by.values()]
    .map(({ dates, ...e }) => ({ ...e, workingDays: dates.size }))
    .sort((a, b) => b.workingDays - a.workingDays || String(a.name || '').localeCompare(String(b.name || '')));
}

// 1-2-5 step for about four gridlines under the tallest bar.
function niceStep(x) {
  if (!(x > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(x));
  const f = x / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p;
}

function whenOf(dn, year) {
  const day = +isoOf(dn).slice(8, 10);
  const part = t(day <= 10 ? 'prr.early' : day <= 20 ? 'prr.mid' : 'prr.late');
  return t('prr.when', { part, month: fmtDate(isoOf(dn), year ? { month: 'long', year: 'numeric' } : { month: 'long' }) });
}

/**
 * Everything one page shows for one item. `opts.itemName` names an item the
 * catalogue does not know (the Production tab's own "Unidentified item").
 * Returns `{ empty: true, item }` when the job has no production of it — the
 * caller says so in words instead of drawing a page of zeros.
 */
export function buildProductionReport(rows, materialTypeId, opts = {}) {
  const qtyByDn = new Map();
  const reportIds = new Set();
  const anyReportDn = new Set();
  const crewFirst = new Map();   // crew → first date it reported the item
  let name = null, unit = null;
  for (const r of rows || []) {
    if (!ISO.test(r.date || '')) continue;
    const dn = dnOf(r.date);
    anyReportDn.add(dn);
    let q = 0;
    for (const i of r.items || []) {
      if (i.materialTypeId !== materialTypeId) continue;
      if (name == null && i.name) name = i.name;
      if (unit == null && i.unit) unit = i.unit;
      q += num(i.quantity);
    }
    if (!(q > 0)) continue;
    qtyByDn.set(dn, (qtyByDn.get(dn) || 0) + q);
    reportIds.add(r.id);
    if (r.who && !(crewFirst.get(r.who) <= dn)) crewFirst.set(r.who, dn);
  }
  const crews = [...crewFirst].sort((a, b) => a[1] - b[1]).map(([who]) => who);
  const item = { materialTypeId, name: name || opts.itemName || '', unit: unit || '' };
  if (!qtyByDn.size) return { empty: true, item };

  const U = n => withUnit(n, item.unit);
  const dns = [...qtyByDn.keys()].sort((a, b) => a - b);
  const first = dns[0], last = dns[dns.length - 1];
  const span = last - first + 1;
  const days = dns.map(dn => ({ date: isoOf(dn), dn, idx: dn - first, qty: qtyByDn.get(dn), label: false }));
  const total = days.reduce((s, d) => s + d.qty, 0);
  const workingDays = days.length;
  const perDay = total / workingDays;
  const multiYear = isoOf(first).slice(0, 4) !== isoOf(last).slice(0, 4);
  // A job that crosses New Year names the year on every date it prints, or
  // "Best day · June 9" could be either June.
  const Y = multiYear ? { year: 'numeric' } : {};
  const dayLong = iso => fmtDate(iso, { month: 'long', day: 'numeric', ...Y });
  const rangeOf = (a, b) => fmtDateRange(a, b, { month: 'short', day: 'numeric', ...Y });

  // ── Trailing 4-week average — full windows only ────────────────────────────
  const windowOf = endDn => {
    const inWin = days.filter(d => d.dn > endDn - WINDOW && d.dn <= endDn);
    const units = inWin.reduce((s, d) => s + d.qty, 0);
    return { inWin, units, perDay: units / inWin.length };
  };
  const trailing = days.filter(d => d.idx >= WINDOW - 1)
    .map(d => ({ idx: d.idx, dn: d.dn, value: windowOf(d.dn).perDay }));

  const w = windowOf(last);
  const short = span < WINDOW;
  // Two figures that print the same are in line, whatever the decimals say —
  // never "averaged 50 … above the job average of 50".
  const sameShown = fmtQty(w.perDay) === fmtQty(perDay);
  const recent = {
    from: isoOf(w.inWin[0].dn), to: isoOf(last), units: w.units, days: w.inWin.length,
    perDay: w.perDay, short,
    pct: short || sameShown ? 0 : Math.round((w.perDay / perDay - 1) * 100),
  };
  // The extremes the line came from, never its own last point.
  const prior = trailing.slice(0, -1);
  const low = prior.length ? prior.reduce((a, b) => (b.value < a.value ? b : a)) : null;
  const high = prior.length ? prior.reduce((a, b) => (b.value > a.value ? b : a)) : null;

  // ── Best day, labelled days ────────────────────────────────────────────────
  const best = days.reduce((a, b) => (b.qty > a.qty ? b : a));
  const bestDays = days.filter(d => d.qty === best.qty);      // a tie is named as one
  // Only days above the job average earn a label: a "50" over a short bar
  // says nothing the axis does not.
  const picked = [];
  for (const d of days.filter(x => x.qty > perDay).sort((a, b) => b.qty - a.qty || a.dn - b.dn)) {
    if (picked.length >= TOP_LABELS) break;
    if (picked.every(p => Math.abs(p.dn - d.dn) >= LABEL_SPACING)) { picked.push(d); d.label = true; }
  }

  // ── Reporting gaps ─────────────────────────────────────────────────────────
  const gaps = [];
  for (let i = 1; i < dns.length; i++) {
    const a = dns[i - 1] + 1, b = dns[i] - 1;
    let weekdays = 0, anyReports = false;
    for (let dn = a; dn <= b; dn++) {
      if (dow(dn) < 5) weekdays++;
      if (anyReportDn.has(dn)) anyReports = true;
    }
    if (weekdays < GAP_WEEKDAYS) continue;
    gaps.push({ from: isoOf(a), to: isoOf(b), startIdx: a - first, endIdx: b - first, anyReports,
      range: fmtDateRange(isoOf(a), isoOf(b)), noteRange: rangeOf(isoOf(a), isoOf(b)),
      label: t(anyReports ? 'prr.gapItem' : 'prr.gapNone') });
  }

  // ── Months ─────────────────────────────────────────────────────────────────
  const months = [];
  for (let ms = monthStartDn(first); ms <= last; ms = nextMonthDn(ms)) {
    const me = nextMonthDn(ms) - 1;
    const from = Math.max(ms, first), to = Math.min(me, last);
    const inMonth = days.filter(d => d.dn >= from && d.dn <= to);
    const units = inMonth.reduce((s, d) => s + d.qty, 0);
    const full = from === ms && to === me;
    months.push({
      key: isoOf(ms).slice(0, 7),
      label: full
        ? fmtDate(isoOf(ms), multiYear ? { month: 'long', year: 'numeric' } : { month: 'long' })
        : fmtDateRange(isoOf(from), isoOf(to), multiYear
          ? { month: 'long', day: 'numeric', year: 'numeric' } : { month: 'long', day: 'numeric' }),
      axisLabel: fmtDate(isoOf(ms), multiYear ? { month: 'short', year: 'numeric' } : { month: 'long' }),
      shortLabel: fmtDate(isoOf(ms), { month: 'short' }),
      startIdx: from - first, endIdx: to - first, startsHere: ms >= first,
      days: inMonth.length, units, perDay: inMonth.length ? units / inMonth.length : null,
    });
  }

  // ── Weeks (Mon–Sun) ────────────────────────────────────────────────────────
  const weeks = [];
  for (let m = mondayOf(first); m <= mondayOf(last); m += 7) {
    const inWeek = days.filter(d => d.dn >= m && d.dn <= m + 6);
    const prev = weeks[weeks.length - 1];
    const monthKey = isoOf(m).slice(0, 7);
    weeks.push({
      monday: isoOf(m), qty: inWeek.reduce((s, d) => s + d.qty, 0), days: inWeek.length,
      range: inWeek.length ? rangeOf(inWeek[0].date, inWeek[inWeek.length - 1].date) : '',
      monthLabel: !prev || prev.monday.slice(0, 7) !== monthKey ? fmtDate(isoOf(m), { month: 'short' }) : '',
      recent: false, label: false,
    });
  }
  weeks.slice(-4).forEach(wk => { wk.recent = true; });
  const workedWeeks = weeks.filter(wk => wk.qty > 0);
  const bestWeek = workedWeeks.length >= 2 ? workedWeeks.reduce((a, b) => (b.qty > a.qty ? b : a)) : null;
  if (bestWeek) bestWeek.label = true;
  if (weeks.length > 1) weeks[weeks.length - 1].label = true;

  // ── Chart scale ────────────────────────────────────────────────────────────
  const maxQty = best.qty;
  const step = niceStep(maxQty / 4);
  const ticks = [];
  for (let v = step; v <= maxQty; v += step) ticks.push({ value: v, label: fmtQty(v) });

  // ── Words ──────────────────────────────────────────────────────────────────
  const itemName = item.name;
  const longDate = iso => fmtDate(iso, { month: 'long', day: 'numeric', year: 'numeric' });
  const subtitle = workingDays === 1
    ? t('prr.subtitleOne', { item: itemName, date: longDate(isoOf(first)) })
    : t('prr.subtitle', { item: itemName,
      range: fmtDateRange(isoOf(first), isoOf(last), { month: 'long', day: 'numeric', year: 'numeric' }) });

  const cmpKey = recent.pct > 0 ? 'above' : recent.pct < 0 ? 'below' : 'equal';
  // "Up from a low" only above the average and only from a real low the line
  // has since climbed out of; "down from a high" the mirror. In line: no trend.
  const ext = cmpKey === 'above' ? low : cmpKey === 'below' ? high : null;
  const mark = !short && ext && fmtQty(ext.value) !== fmtQty(recent.perDay)
    && (cmpKey === 'above' ? ext.value < recent.perDay : ext.value > recent.perDay) ? ext : null;
  let headline;
  if (workingDays === 1) {
    headline = t('prr.headlineOne', { qty: U(total), date: dayLong(isoOf(first)) });
  } else if (short) {
    headline = t('prr.headlineShort', { days: workingDays, avg: U(perDay) });
  } else {
    const trend = mark
      ? t(cmpKey === 'above' ? 'prr.trendUp' : 'prr.trendDown', { v: fmtQty(mark.value), when: whenOf(mark.dn, multiYear) })
      : '';
    // The window is named by its end date: on a job whose last report is weeks
    // old, "the last four weeks" would read as the weeks before the issue date.
    headline = t('prr.headline', { to: dayLong(recent.to), recent: U(recent.perDay), cmp: t('prr.cmp_' + cmpKey), avg: fmtQty(perDay), trend });
  }
  const legend = t(trailing.length ? 'prr.legend' : 'prr.legendShort', { avg: fmtQty(perDay) });

  const pctChip = recent.short ? '' : (recent.pct > 0 ? '+' : recent.pct < 0 ? '−' : '') + Math.abs(recent.pct) + '%';
  const unitTxt = plainUnit(item.unit) ? '' : item.unit;
  // The red accent flags the recent trend; under four weeks there is none, so
  // that tile is plain. One working day: average and best ARE the total.
  const tiles = [
    { value: fmtQty(total), unit: unitTxt, label: t('prr.tileTotal', { item: itemName }) },
    { value: fmtNumber(workingDays), unit: '', label: t(workingDays === 1 ? 'prr.tileDaysOne' : 'prr.tileDays') },
  ];
  if (workingDays > 1) tiles.push(
    { value: fmtQty(perDay), unit: unitTxt, label: t('prr.tileAvg') },
    // trend: a pace that rose reads green, a fall keeps the red, level is plain (T8).
    { value: fmtQty(recent.perDay), unit: unitTxt, chip: pctChip, accent: !recent.short,
      trend: recent.short ? null : recent.pct > 0 ? 'up' : recent.pct < 0 ? 'down' : 'flat',
      label: recent.short ? t('prr.tileRecentShort')
        : t('prr.tileRecent', { range: rangeOf(recent.from, recent.to) }) },
    { value: fmtQty(best.qty), unit: unitTxt,
      label: bestDays.length > 1 ? t('prr.tileBestTied', { date: dayLong(best.date), n: bestDays.length })
        : t('prr.tileBest', { date: dayLong(best.date) }) });

  // Notes: each is a list of [bold lead, text] runs.
  const notes = [];
  const bestRuns = [];
  if (bestWeek) bestRuns.push([t('prr.noteBestWeek'), t('prr.noteBestWeekText', { range: bestWeek.range, qty: U(bestWeek.qty) })]);
  const listOf = arr => new Intl.ListFormat('en', { type: 'conjunction' }).format(arr);
  if (workingDays > 1 && bestDays.length === 1) {
    bestRuns.push([t('prr.noteBestDay'), t('prr.noteBestDayText', { date: rangeOf(best.date, best.date), qty: U(best.qty) })]);
  } else if (workingDays > 1 && bestDays.length <= 3) {
    bestRuns.push([t('prr.noteBestDays'), t('prr.noteBestDaysText', { dates: listOf(bestDays.map(d => rangeOf(d.date, d.date))), qty: U(best.qty) })]);
  } else if (workingDays > 1) {
    // A flat job ties every day; listing them all would be the whole calendar.
    bestRuns.push([t('prr.noteBestDay'), t('prr.noteBestDayMany', { qty: U(best.qty), n: bestDays.length, date: rangeOf(best.date, best.date) })]);
  }
  if (bestRuns.length) notes.push(bestRuns);
  if (!recent.short) {
    notes.push([[t('prr.noteRecent'), t('prr.noteRecentText', {
      qty: U(recent.units), range: rangeOf(recent.from, recent.to),
      days: recent.days === 1 ? t('prr.oneWorkingDay') : t('prr.nWorkingDays', { n: recent.days }),
      cmp: cmpKey === 'equal' ? t('prr.noteCmpEqual') : t('prr.noteCmp_' + cmpKey, { pct: Math.abs(recent.pct) }),
    })]]);
  }
  if (gaps.length) {
    // "No reports at all" and "reports, but not of this item" are different
    // facts, so each gets its own clause, each naming its own ranges.
    const ranges = list => listOf(list.map(g => g.noteRange));
    const none = gaps.filter(g => !g.anyReports), other = gaps.filter(g => g.anyReports);
    const list = [
      none.length ? t('prr.gapNoteNone', { range: ranges(none) }) : '',
      other.length ? t('prr.gapNoteItem', { item: itemName, range: ranges(other) }) : '',
    ].filter(Boolean).join('; ');
    notes.push([[t(gaps.length === 1 ? 'prr.noteGap' : 'prr.noteGaps'), t('prr.noteGapText', { list })]]);
  }
  // Which weekdays crews actually report on, when that is a plain run (Mon–Sat).
  const wd = [...new Set(dns.map(dow))].sort((a, b) => a - b);
  const wdName = i => fmtDate(isoOf(mondayOf(first) + i), { weekday: 'short' });
  // A lone weekday is only a pattern once it repeats: one report on a Tuesday
  // does not make "(Tue)" the rule.
  const wdRun = wd.length < 7 && wd[wd.length - 1] - wd[0] + 1 === wd.length && (wd.length > 1 || workingDays >= 5)
    ? (wd.length === 1 ? wdName(wd[0]) : `${wdName(wd[0])}–${wdName(wd[wd.length - 1])}`) : '';
  notes.push([['', t(reportIds.size === 1 ? 'prr.noteSourceOne' : 'prr.noteSource', { n: fmtNumber(reportIds.size) })
    + ' ' + (wdRun ? t('prr.noteWorkingDayRun', { days: wdRun }) : t('prr.noteWorkingDay'))]]);

  return {
    empty: false, item,
    firstDate: isoOf(first), lastDate: isoOf(last), span,
    total, workingDays, perDay, reportCount: reportIds.size,
    recent, best: { date: best.date, qty: best.qty }, bestWeek: bestWeek && { monday: bestWeek.monday, range: bestWeek.range, qty: bestWeek.qty },
    crews,
    days, trailing, gaps, months, weeks,
    chart: {
      span, yMax: maxQty * 1.1, ticks,
      avg: perDay, avgLabel: t('prr.avgLabel', { v: fmtQty(perDay) }),
      recentLabel: trailing.length ? t('prr.recentLabel', { v: fmtQty(recent.perDay) }) : '',
      mark: mark && { idx: mark.idx, value: mark.value, label: fmtQty(mark.value) },
    },
    weekMax: Math.max(...weeks.map(wk => wk.qty)),
    text: {
      subtitle, headline, legend, tiles, notes,
      qtyHead: plainUnit(item.unit) ? t('prr.colQty') : t('prr.colQtyUnit', { unit: item.unit }),
      fmt: { qty: fmtQty },
    },
  };
}

/**
 * The page head shared by the screen and the PDF: the title, and the job's
 * own reference line. GC and site only when the project row carries them;
 * crews by name up to two, a count beyond.
 */
export function reportHeader(project, model, today) {
  const p = project || {};
  const crews = (model && model.crews) || [];
  const meta = [];
  const jobNo = jobNumber(p);
  if (jobNo) meta.push([t('prr.metaJob'), jobNo]);
  if (p.site_code) meta.push([t('prr.metaSite'), String(p.site_code)]);
  if (p.gc_company) meta.push([t('prr.metaGc'), String(p.gc_company)]);
  if (crews.length) meta.push([t(crews.length === 1 ? 'prr.metaForeman' : 'prr.metaForemen'),
    crews.length <= 2 ? crews.join(', ') : t('prr.metaCrews', { n: crews.length })]);
  // The issue date is the PDF's alone: on screen the page is live, not issued.
  const issued = today ? [t('prr.metaIssued'), fmtDate(today, { month: 'long', day: 'numeric', year: 'numeric' })] : null;
  return { title: t('prr.title', { job: displayName(p) || t('job.untitled') }), jobName: displayName(p), jobNo, meta, issued };
}

// ── Screen charts (SVG, pixel units — the container's real width) ────────────
// Same idea as pulse-chart.js: drawn at true UI size so the labels stay
// readable at 390px, re-drawn by the caller when the width changes. Colours
// are classes (css/redesign-tokens.css, .prr-*), never literals here.

const escX = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
const r1 = v => Math.round(v * 10) / 10;

/**
 * Axis labels that fit: left to right, a label is kept only when it stays
 * inside [min, max] and clears the last kept one by `gap`. A long job thins
 * to every second or third month instead of losing its whole axis. Used by
 * the screen charts below and the PDF (69-production-report.js) alike.
 * `labels`: [{ left, right, ... }] in axis order.
 */
export function fitLabels(labels, min, max, gap = 4) {
  let edge = -Infinity;
  return labels.filter(l => {
    if (l.left < min - 0.01 || l.right > max + 0.01 || l.left < edge + gap) return false;
    edge = l.right;
    return true;
  });
}

export function dailyChartSvg(m, width) {
  const W = Math.max(280, Math.round(width) || 800);
  const narrow = W < 640;
  const H = narrow ? 230 : 300;
  const padL = 38, padR = narrow ? 8 : 104, top = 20, bottom = 24;
  const plotW = W - padL - padR, plotH = H - top - bottom, base = top + plotH;
  const c = m.chart, dayW = plotW / c.span;
  const barW = Math.min(22, Math.max(1, dayW * 0.72));
  const x = idx => padL + (idx + 0.5) * dayW;
  const y = v => base - (v / c.yMax) * plotH;
  let s = '', gapTxt = '';
  for (const g of m.gaps) {
    const gx = padL + g.startIdx * dayW, gw = (g.endIdx - g.startIdx + 1) * dayW;
    s += `<rect x="${r1(gx)}" y="${top}" width="${r1(gw)}" height="${plotH}" class="prr-gap"/>`;
    // Narrower than its words, the label still names the band: it may spill
    // onto the bars beside it, so it is drawn after them, on a halo (.prr-gap-t).
    if (gw >= 28) gapTxt += `<text x="${r1(gx + gw / 2)}" y="${base - 22}" text-anchor="middle" class="prr-gap-t">${escX(g.label)}</text>`
      + `<text x="${r1(gx + gw / 2)}" y="${base - 9}" text-anchor="middle" class="prr-gap-t">${escX(g.range)}</text>`;
  }
  for (const tk of c.ticks) {
    s += `<line x1="${padL}" x2="${padL + plotW}" y1="${r1(y(tk.value))}" y2="${r1(y(tk.value))}" class="prr-grid"/>`
      + `<text x="${padL - 6}" y="${r1(y(tk.value) + 3.5)}" text-anchor="end" class="prr-tick">${escX(tk.label)}</text>`;
  }
  const monthLbls = [];
  for (const mo of m.months) {
    if (mo.startsHere && mo.startIdx > 0)
      s += `<line x1="${r1(padL + mo.startIdx * dayW)}" x2="${r1(padL + mo.startIdx * dayW)}" y1="${top}" y2="${base}" class="prr-msep"/>`;
    const text = narrow ? mo.shortLabel : mo.axisLabel;
    const cx = padL + (mo.startIdx + (mo.endIdx - mo.startIdx + 1) / 2) * dayW, half = text.length * 3.25;
    monthLbls.push({ text, cx, left: cx - half, right: cx + half });
  }
  for (const l of fitLabels(monthLbls, padL, padL + plotW))
    s += `<text x="${r1(l.cx)}" y="${H - 6}" text-anchor="middle" class="prr-month">${escX(l.text)}</text>`;
  for (const d of m.days) {
    s += `<rect x="${r1(x(d.idx) - barW / 2)}" y="${r1(y(d.qty))}" width="${r1(barW)}" height="${r1(base - y(d.qty))}" class="prr-bar"/>`;
  }
  s += gapTxt;
  const labelled = m.days.filter(d => d.label && (!narrow || d.qty === m.best.qty));
  for (const d of labelled)
    s += `<text x="${r1(x(d.idx))}" y="${r1(y(d.qty) - 5)}" text-anchor="middle" class="prr-barlbl">${escX(m.text.fmt.qty(d.qty))}</text>`;
  s += `<line x1="${padL}" x2="${padL + plotW}" y1="${base}" y2="${base}" class="prr-base"/>`;
  s += `<line x1="${padL}" x2="${padL + plotW}" y1="${r1(y(c.avg))}" y2="${r1(y(c.avg))}" class="prr-avg"/>`;
  if (m.trailing.length) {
    s += `<polyline points="${m.trailing.map(p => `${r1(x(p.idx))},${r1(y(p.value))}`).join(' ')}" class="prr-trend"/>`;
    const lastP = m.trailing[m.trailing.length - 1];
    s += `<circle cx="${r1(x(lastP.idx))}" cy="${r1(y(lastP.value))}" r="3.5" class="prr-trend-dot"/>`;
    if (c.mark) s += `<circle cx="${r1(x(c.mark.idx))}" cy="${r1(y(c.mark.value))}" r="3" class="prr-trend-dot"/>`
      + `<text x="${r1(x(c.mark.idx) - 5)}" y="${r1(y(c.mark.value) - 7)}" text-anchor="end" class="prr-lowlbl">${escX(c.mark.label)}</text>`;
  }
  if (!narrow) {
    // End labels to the right of the plot, nudged apart when the two lines meet.
    let ya = y(c.avg) + 4, yr = m.trailing.length ? y(m.trailing[m.trailing.length - 1].value) + 4 : null;
    if (yr != null && Math.abs(ya - yr) < 13) { if (yr <= ya) { yr = (ya + yr) / 2 - 7; ya = yr + 14; } else { ya = (ya + yr) / 2 - 7; yr = ya + 14; } }
    s += `<text x="${padL + plotW + 6}" y="${r1(ya)}" class="prr-avglbl">${escX(c.avgLabel)}</text>`;
    if (yr != null) s += `<text x="${padL + plotW + 6}" y="${r1(yr)}" class="prr-trendlbl">${escX(c.recentLabel)}</text>`;
  }
  return `<svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" role="img" aria-label="${escX(m.text.headline)}" class="prr-svg">${s}</svg>`;
}

export function weeklyChartSvg(m, width) {
  // Drawn at the box's own width, never wider: a floor here overflowed the
  // 209px column at 1024 and ran its label into the notes.
  const W = Math.round(width) || 320, H = 130;
  const top = 16, bottom = 16, plotH = H - top - bottom, base = top + plotH;
  const n = m.weeks.length, slot = W / n, barW = Math.max(1, Math.min(26, slot * 0.7));
  // Labels stay inside the box: the latest week is the last slot, and its
  // number would otherwise hang half past the right edge.
  const inside = (cx, half) => Math.min(W - half, Math.max(half, cx));
  const y = v => base - (m.weekMax ? v / m.weekMax : 0) * plotH * 0.88;
  let s = '';
  // Each month's name sits at its first week, pulled in at the right edge.
  const monthLbls = fitLabels(m.weeks.map((wk, i) => {
    if (!wk.monthLabel) return null;
    const w = wk.monthLabel.length * 6.5, left = Math.min(i * slot + 1, W - w);
    return { text: wk.monthLabel, left, right: left + w };
  }).filter(Boolean), 0, W);
  const firstRecent = m.weeks.findIndex(wk => wk.recent);
  if (firstRecent >= 0) s += `<rect x="${r1(firstRecent * slot)}" y="${top - 4}" width="${r1((n - firstRecent) * slot)}" height="${plotH + 4}" class="prr-gap"/>`;
  m.weeks.forEach((wk, i) => {
    const cx = (i + 0.5) * slot;
    if (wk.qty > 0) s += `<rect x="${r1(cx - barW / 2)}" y="${r1(y(wk.qty))}" width="${r1(barW)}" height="${r1(base - y(wk.qty))}" class="${wk.recent ? 'prr-wbar prr-wbar--recent' : 'prr-wbar'}"/>`;
    const lbl = m.text.fmt.qty(wk.qty);
    if (wk.label && wk.qty > 0) s += `<text x="${r1(inside(cx, lbl.length * 3.4))}" y="${r1(y(wk.qty) - 4)}" text-anchor="middle" class="prr-barlbl">${escX(lbl)}</text>`;
  });
  for (const l of monthLbls) s += `<text x="${r1(l.left)}" y="${H - 3}" class="prr-wmonth">${escX(l.text)}</text>`;
  s += `<line x1="0" x2="${W}" y1="${base}" y2="${base}" class="prr-base"/>`;
  return `<svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" role="img" aria-label="${escX(t('prr.byWeek'))}" class="prr-svg">${s}</svg>`;
}
